import type { FieldPath } from 'react-hook-form';

import type {
  Cooling_Enum_Enum,
  GetPropertyQuery,
  Heating_Distribution_Enum_Enum,
  Heating_Enum_Enum,
  Properties_Insert_Input,
  Properties_Set_Input,
  PropertyFormDetailsFragment,
} from '@graphql/__generated__/graphql';
import {
  type FormDefinitionType,
  type FormFieldDefinitionType,
  createCategoryElement,
  createCheckboxElement,
  createCheckboxGroupElement,
  createNumberFieldElement,
  createSelectElement,
  createTextFieldElement,
} from '@modules/_shared/components/form/RaForm';
import {
  createSurfaceFieldElement,
  getCompactGridProps,
  getRatingChoiceOptions,
} from '@modules/_shared/components/property-form/forms-definitions/utils';
import type { PropertyTypeLight } from '@modules/_shared/components/property-form/propertyFormQueries';
import {
  getCurrencySymbol,
  getRatingLabel,
} from '@modules/_shared/utils/formatting';
import {
  isAppt,
  isCommercialBuilding,
  isHouseOrAppt,
  isMixedUseBuilding,
  isResidentialBuilding,
} from '@modules/_shared/utils/propertyDataChecks';
import {
  getEnergyClassValues,
  getPropertyCoolingLabel,
  getPropertyHeatingDistributionLabel,
  getPropertyHeatingLabel,
  getPropertySides,
} from '@modules/_shared/utils/propertyTemplateFunctions';
import { isLandTypeProperty } from '@modules/_shared/utils/propertyTypes';
import type { IntlLocale, Translate } from '@shared/hooks/locale';
import { type Currency, getCurrencyByCountryCode } from '@shared/LocaleContext';

export type PropertyFormDetailsData = Pick<
  Properties_Insert_Input,
  keyof Omit<PropertyFormDetailsFragment, '__property_type'>
> & {
  __main_type?: string | null;
  __property_type?: PropertyTypeLight;
};

export type TranslatedEnumOption = { value: string; label?: string | null };

export type PropertyFormContext = {
  propertyTypes: GetPropertyQuery['property_types'];
  heatingTypes: GetPropertyQuery['heating_enum'];
  heatingDistribution: GetPropertyQuery['heating_distribution_enum'];
  coolingTypes: GetPropertyQuery['cooling_enum'];
  bedTypeOptions?: TranslatedEnumOption[];
  windowViewOptions?: TranslatedEnumOption[];
  tenantGenderOptions?: TranslatedEnumOption[];
  propertyId?: string;
  readOnly?: boolean;
};

type PropertyType = PropertyFormContext['propertyTypes'][number];

export const createPropertyTypeOptions = (
  propertyTypes: PropertyFormContext['propertyTypes'],
  filterCondition: (pt: PropertyType) => boolean,
  valueKey: keyof PropertyType,
): Array<{ value: string; label: string }> =>
  propertyTypes
    .filter(filterCondition)
    .map(pt => ({
      value: pt[valueKey] ?? '',
      label: pt.label ?? '',
    }))
    .sort((a, b) => (a.label ?? '').localeCompare(b.label ?? ''));

const getDetailsFields = (
  t: Translate,
  context: PropertyFormContext,
  useMinergieCode: boolean,
  currency: string | null | undefined,
  countryCode: string,
  roomsAllowsDecimals: boolean,
  locale: IntlLocale,
  compactMode?: boolean,
): FormFieldDefinitionType<PropertyFormDetailsData>[] => {
  const displayCurrency = (currency ??
    getCurrencyByCountryCode(countryCode)) as Currency;

  const formDefinition: FormFieldDefinitionType<PropertyFormDetailsData>[] = [
    // *******************
    // PROPERTY TYPE
    // *******************
    createCategoryElement('property_type_title', t('Property type')),
    createSelectElement('__main_type', t('Property main type'), () =>
      createPropertyTypeOptions(
        context.propertyTypes,
        pt => pt.type === 'main',
        'name',
      ),
    ),
    createSelectElement(
      'property_type_id',
      t('Property type'),
      ({ __main_type }) =>
        createPropertyTypeOptions(
          context.propertyTypes,
          pt => pt.main_type === __main_type && pt.type === 'simple',
          'id',
        ),
    ),
    createTextFieldElement('plot_number', t('plotNumberLabel')),

    // *******************
    // SURFACES
    // *******************
    createCategoryElement('surfaces_title', t('Surfaces')),
    createSurfaceFieldElement(
      'residential_surface',
      t('Residential surface'),
      undefined,
      getCompactGridProps(compactMode),
    ),
    createSurfaceFieldElement(
      'commercial_surface',
      t('Commercial surface'),
      undefined,
      getCompactGridProps(compactMode),
    ),
    createSurfaceFieldElement(
      'living_surface',
      t('Living surface'),
      undefined,
      getCompactGridProps(compactMode),
    ),
    createSurfaceFieldElement(
      'built_surface',
      t('Built surface'),
      undefined,
      getCompactGridProps(compactMode),
    ),
    createSurfaceFieldElement(
      'usable_surface',
      t('Usable surface'),
      undefined,
      getCompactGridProps(compactMode),
    ),
    createSurfaceFieldElement(
      'gross_floor_surface',
      t('Gross floor surface'),
      undefined,
      getCompactGridProps(compactMode),
    ),
    createSurfaceFieldElement(
      'balcony_surface',
      t('Balcony surface'),
      undefined,
      getCompactGridProps(compactMode),
    ),
    createSurfaceFieldElement(
      'terrace_surface',
      t('Terrace surface'),
      undefined,
      getCompactGridProps(compactMode),
    ),
    createSurfaceFieldElement(
      'garden_surface',
      t('Garden surface'),
      undefined,
      getCompactGridProps(compactMode),
    ),
    createSurfaceFieldElement(
      'land_surface',
      t('Land surface'),
      undefined,
      getCompactGridProps(compactMode),
    ),
    createSurfaceFieldElement(
      'basement_surface',
      t('Basement surface'),
      undefined,
      getCompactGridProps(compactMode),
    ),
    createSurfaceFieldElement(
      'weighted_floor_surface',
      t('Weighted floor surface'),
      undefined,
      getCompactGridProps(compactMode),
    ),
    createSurfaceFieldElement(
      'building_volume',
      t('Building volume'),
      undefined,
      getCompactGridProps(compactMode),
    ),

    // *******************
    // UNITS
    // *******************
    createCategoryElement('units_title', t('Units')),
    createNumberFieldElement(
      'number_of_residential_units',
      t('numberOfResidentialUnits'),
      {
        min: 1,
        max: 500,
        gridProps: getCompactGridProps(compactMode),
      },
    ),
    createNumberFieldElement(
      'number_of_commercial_units',
      t('numberOfCommercialUnits'),
      {
        min: 1,
        max: 500,
        gridProps: getCompactGridProps(compactMode),
      },
    ),

    // *******************
    // ROOMS
    // *******************
    createCategoryElement('rooms_title', t('Rooms')),
    createNumberFieldElement('number_of_rooms', t('Number of rooms'), {
      gridProps: { xs: 6 },
      decimalNumbers: roomsAllowsDecimals ? 1 : 0,
    }),
    createNumberFieldElement('number_of_bedrooms', t('Number of bedrooms'), {
      gridProps: { xs: 6 },
    }),
    createNumberFieldElement('number_of_bathrooms', t('Number of bathrooms'), {
      gridProps: { xs: 6 },
    }),
    createNumberFieldElement('number_of_toilets', t('Number of toilets'), {
      gridProps: { xs: 6 },
    }),

    // *******************
    // FLOORS
    // *******************
    createCategoryElement('floors_title', t('floors')),
    createNumberFieldElement('number_of_floors', t('numberOfFloors'), {
      min: 0,
      max: 50,
    }),
    createNumberFieldElement('floor_of_flat', t('floorOfFlat'), {
      min: 0,
      max: 50,
    }),
    createNumberFieldElement('number_of_lifts', t('Number of elevators'), {
      min: 1,
      max: 50,
    }),
    createSelectElement('side', t('side'), () => getPropertySides(t), {
      gridProps: { alignSelf: 'flex-start' },
    }),
    createCheckboxGroupElement('orientation', t('Orientation'), [
      createCheckboxElement('is_oriented_south', t('South')),
      createCheckboxElement('is_oriented_west', t('West')),
      createCheckboxElement('is_oriented_north', t('North')),
      createCheckboxElement('is_oriented_east', t('East')),
    ]),

    // *******************
    // PARKING
    // *******************
    createCategoryElement('parking_title', t('parking')),
    createNumberFieldElement('number_of_indoor_parkings', t('indoorParking'), {
      min: 0,
      gridProps: { xs: 6 },
    }),
    createNumberFieldElement(
      'number_of_outdoor_parkings',
      t('outdoorParking'),
      {
        min: 0,
        gridProps: { xs: 6 },
      },
    ),

    // *******************
    // HVAC
    // *******************
    createCategoryElement(
      'hvac_title',
      t('Heating, ventilation and air conditioning'),
    ),
    createSelectElement('heating', t('Heating'), () =>
      context.heatingTypes.map(he => ({
        value: he.value,
        label:
          getPropertyHeatingLabel(he.value as Heating_Enum_Enum, t) ?? he.value,
      })),
    ),
    createSelectElement('heating_distribution', t('Heating distribution'), () =>
      context.heatingDistribution.map(hed => ({
        value: hed.value,
        label:
          getPropertyHeatingDistributionLabel(
            hed.value as Heating_Distribution_Enum_Enum,
            t,
          ) ?? hed.value,
      })),
    ),
    createSelectElement('cooling', t('Cooling'), () =>
      context.coolingTypes.map(cool => ({
        value: cool.value,
        label:
          getPropertyCoolingLabel(cool.value as Cooling_Enum_Enum, t) ??
          cool.value,
      })),
    ),
  ];

  if (!useMinergieCode) {
    const currentYear = new Date().getFullYear();

    formDefinition.push(
      createCategoryElement('energy_efficiency_title', t('energyEfficiency')),
      createSelectElement(
        'energy_consumption_class',
        t('energyConsumptionClass'),
        () => getEnergyClassValues(t),
        { gridProps: getCompactGridProps(compactMode) },
      ),
      createSelectElement(
        'gas_emissions_class',
        t('gasEmissionsClass'),
        () => getEnergyClassValues(t),
        { gridProps: getCompactGridProps(compactMode) },
      ),
      createNumberFieldElement('consumption', t('consumption'), {
        suffix: 'kWh/m²/y',
        gridProps: getCompactGridProps(compactMode),
      }),
      createNumberFieldElement('emissions', t('emissions'), {
        suffix: 'kg CO2/m²/y',
        gridProps: getCompactGridProps(compactMode),
      }),
      createNumberFieldElement('energy_reference_year', t('estimateRefYear'), {
        max: currentYear,
        min: 1000,
        gridProps: getCompactGridProps(compactMode),
        disableFormatting: true,
      }),
      {
        type: 'date',
        name: 'energy_inspection_date',
        label: t('inspectionDate'),
        gridProps: getCompactGridProps(compactMode),
      },
      createNumberFieldElement(
        'estimated_lower_energy_cost',
        t('minEstimatedCostYear'),
        {
          prefix: getCurrencySymbol(displayCurrency, locale),
          decimalNumbers: 2,
          gridProps: getCompactGridProps(compactMode),
        },
      ),
      createNumberFieldElement(
        'estimated_higher_energy_cost',
        t('maxEstimatedCostYear'),
        {
          prefix: getCurrencySymbol(displayCurrency, locale),
          decimalNumbers: 2,
          gridProps: getCompactGridProps(compactMode),
        },
      ),
      createTextFieldElement(
        'energy_certificate_version',
        t('certificateVersion'),
        { gridProps: getCompactGridProps(compactMode) },
      ),
    );
  }

  formDefinition.push(
    // *******************
    // CONSTRUCTION
    // *******************
    createCategoryElement('construction_title', t('Construction')),
    createNumberFieldElement('construction_year', t('Construction year'), {
      disableFormatting: true,
      gridProps: getCompactGridProps(compactMode, 6),
    }),
    createNumberFieldElement('renovation_year', t('Renovation year'), {
      disableFormatting: true,
      gridProps: getCompactGridProps(compactMode, 6),
    }),
    {
      ...createSelectElement('building_standard', t('buildingStandard'), () =>
        getRatingChoiceOptions(t),
      ),
      emptyValueAsNull: true,
    },
    {
      ...createSelectElement('building_condition', t('buildingCondition'), () =>
        getRatingChoiceOptions(t),
      ),
      emptyValueAsNull: true,
    },
  );

  if (useMinergieCode) {
    formDefinition.push({
      ...createSelectElement('minergie_code', t('minergieCode'), () => [
        { value: 5, label: t('fiveMinergiePEco') },
        { value: 4, label: t('fourMinergieEco') },
        { value: 3, label: t('threeMinergieP') },
        { value: 2, label: t('twoMinergie') },
        { value: 1, label: t('oneNotMinergie') },
        { value: null, label: t('Not set') },
      ]),
      emptyValueAsNull: true,
    });
  }

  formDefinition.push(
    // *******************
    // LOCATION
    // *******************
    createCategoryElement('location_title', t('location')),
    createNumberFieldElement('micro_location', t('microLocation'), {
      min: 1,
      max: 5,
      decimalNumbers: 1,
      helpText: ({ micro_location }) =>
        micro_location == null
          ? t('Enter a value between 1 to 5')
          : getRatingLabel(micro_location, t) ?? '',
    }),
    createNumberFieldElement('macro_location', t('macroLocation'), {
      min: 1,
      max: 5,
      decimalNumbers: 1,
      helpText: ({ macro_location }) =>
        macro_location == null
          ? t('Enter a value between 1 to 5')
          : getRatingLabel(macro_location, t) ?? '',
    }),
  );

  return formDefinition;
};

const isCommercial = (propertyType: PropertyTypeLight) =>
  isCommercialBuilding(propertyType) || isMixedUseBuilding(propertyType);

const isResidential = (propertyType: PropertyTypeLight) =>
  isResidentialBuilding(propertyType) || isMixedUseBuilding(propertyType);

export type FieldVisibilityContext = {
  isTransaction?: boolean;
};

export const propertyDetailsConditionalFields = new Map<
  | FieldPath<PropertyFormDetailsData>
  | 'orientation'
  | 'rooms_title'
  | 'floors_title'
  | 'parking_title'
  | 'hvac_title'
  | 'energy_efficiency_title'
  | 'construction_title'
  | 'units_title',
  (data: PropertyFormDetailsData, context?: FieldVisibilityContext) => boolean
>([
  ['plot_number', ({ __main_type }) => __main_type === 'PROP'],
  [
    'living_surface',
    ({ __property_type }) =>
      isHouseOrAppt(__property_type) || isMixedUseBuilding(__property_type),
  ],
  [
    'usable_surface',
    ({ __property_type }, context) => {
      // Transactions: only show for INDUS
      if (context?.isTransaction) {
        return __property_type?.main_type === 'INDUS';
      }
      // Property form: show for many types
      return (
        isHouseOrAppt(__property_type) ||
        isMixedUseBuilding(__property_type) ||
        isCommercialBuilding(__property_type) ||
        // Legacy to remove
        ['GASTRO', 'INDUS', 'PARK', 'SECONDARY'].includes(
          __property_type?.main_type ?? '',
        )
      );
    },
  ],
  [
    'gross_floor_surface',
    ({ __property_type }) =>
      isHouseOrAppt(__property_type) ||
      isMixedUseBuilding(__property_type) ||
      isCommercialBuilding(__property_type) ||
      // Legacy to remove
      ['GASTRO', 'INDUS'].includes(__property_type?.main_type ?? ''),
  ],
  [
    'balcony_surface',
    ({ __property_type }) =>
      isHouseOrAppt(__property_type) ||
      isMixedUseBuilding(__property_type) ||
      isCommercialBuilding(__property_type),
  ],
  [
    'terrace_surface',
    ({ __property_type }) =>
      isHouseOrAppt(__property_type) ||
      isMixedUseBuilding(__property_type) ||
      isCommercialBuilding(__property_type),
  ],
  [
    'garden_surface',
    ({ __property_type }) =>
      isHouseOrAppt(__property_type) ||
      isMixedUseBuilding(__property_type) ||
      isCommercialBuilding(__property_type),
  ],
  [
    'land_surface',
    ({ __property_type }, context) => {
      // Transactions: hide for APPT and INDUS
      if (context?.isTransaction) {
        return (
          !isAppt(__property_type) && __property_type?.main_type !== 'INDUS'
        );
      }
      // Property form: hide only for APPT
      return !isAppt(__property_type);
    },
  ],
  [
    'basement_surface',
    ({ __property_type }) =>
      isHouseOrAppt(__property_type) ||
      isMixedUseBuilding(__property_type) ||
      isCommercialBuilding(__property_type),
  ],
  [
    'weighted_floor_surface',
    ({ __property_type }) =>
      isHouseOrAppt(__property_type) ||
      isMixedUseBuilding(__property_type) ||
      isCommercialBuilding(__property_type),
  ],
  [
    'building_volume',
    ({ __property_type }) =>
      isHouseOrAppt(__property_type) ||
      isMixedUseBuilding(__property_type) ||
      isCommercialBuilding(__property_type) ||
      // Legacy to remove
      ['AGRI', 'GASTRO', 'INDUS', 'PARK'].includes(
        __property_type?.main_type ?? '',
      ),
  ],
  [
    'built_surface',
    ({ __property_type }) =>
      isHouseOrAppt(__property_type) ||
      isMixedUseBuilding(__property_type) ||
      isCommercialBuilding(__property_type) ||
      // Legacy to remove
      ['AGRI', 'GASTRO', 'INDUS', 'PARK'].includes(
        __property_type?.main_type ?? '',
      ),
  ],
  [
    'residential_surface',
    ({ __property_type }) => isResidential(__property_type),
  ],
  [
    'commercial_surface',
    ({ __property_type }) => isCommercial(__property_type),
  ],
  [
    'units_title',
    ({ __property_type }) =>
      isResidential(__property_type) || isCommercial(__property_type),
  ],
  [
    'number_of_residential_units',
    ({ __property_type }) => isResidential(__property_type),
  ],
  [
    'number_of_commercial_units',
    ({ __property_type }) => isCommercial(__property_type),
  ],
  [
    'rooms_title',
    ({ __property_type }) =>
      isHouseOrAppt(__property_type, true) || isCommercial(__property_type),
  ],
  [
    'number_of_rooms',
    ({ __property_type }) => isHouseOrAppt(__property_type, true),
  ],
  [
    'number_of_bedrooms',
    ({ __property_type }) => isHouseOrAppt(__property_type, true),
  ],
  [
    'number_of_bathrooms',
    ({ __property_type }) =>
      isHouseOrAppt(__property_type, true) || isCommercial(__property_type),
  ],
  [
    'number_of_toilets',
    ({ __property_type }) =>
      isHouseOrAppt(__property_type, true) || isCommercial(__property_type),
  ],
  [
    'floors_title',
    ({ __property_type }) => !isLandTypeProperty(__property_type),
  ],
  [
    'number_of_floors',
    ({ __property_type }) =>
      isHouseOrAppt(__property_type) ||
      isMixedUseBuilding(__property_type) ||
      isCommercialBuilding(__property_type) ||
      // Legacy to remove
      !isLandTypeProperty(__property_type),
  ],
  ['floor_of_flat', ({ __property_type }) => isAppt(__property_type)],
  [
    'number_of_lifts',
    ({ __property_type }) => __property_type?.name === 'indus_office',
  ],
  [
    'side',
    ({ __property_type }) =>
      isAppt(__property_type) || __property_type?.name === 'indus_office',
  ],
  ['orientation', ({ __property_type }) => isHouseOrAppt(__property_type)],
  [
    'parking_title',
    ({ __property_type }) => !isLandTypeProperty(__property_type),
  ],
  [
    'number_of_indoor_parkings',
    ({ __property_type }) =>
      isHouseOrAppt(__property_type) ||
      isMixedUseBuilding(__property_type) ||
      isCommercialBuilding(__property_type) ||
      // Legacy to remove
      !isLandTypeProperty(__property_type),
  ],
  [
    'number_of_outdoor_parkings',
    ({ __property_type }) =>
      isHouseOrAppt(__property_type) ||
      isMixedUseBuilding(__property_type) ||
      isCommercialBuilding(__property_type) ||
      // Legacy to remove
      !isLandTypeProperty(__property_type),
  ],
  ['hvac_title', ({ __property_type }) => !isLandTypeProperty(__property_type)],
  [
    'heating',
    ({ __property_type }) =>
      isHouseOrAppt(__property_type) ||
      isMixedUseBuilding(__property_type) ||
      isCommercialBuilding(__property_type) ||
      // Legacy to remove
      !isLandTypeProperty(__property_type),
  ],
  [
    'heating_distribution',
    ({ __property_type }) =>
      isHouseOrAppt(__property_type) ||
      isMixedUseBuilding(__property_type) ||
      isCommercialBuilding(__property_type) ||
      // Legacy to remove
      !isLandTypeProperty(__property_type),
  ],
  [
    'cooling',
    ({ __property_type }) =>
      isHouseOrAppt(__property_type) ||
      isMixedUseBuilding(__property_type) ||
      isCommercialBuilding(__property_type) ||
      // Legacy to remove
      !isLandTypeProperty(__property_type),
  ],
  [
    'energy_efficiency_title',
    ({ __property_type }) => !isLandTypeProperty(__property_type),
  ],
  [
    'energy_consumption_class',
    ({ __property_type }) =>
      isHouseOrAppt(__property_type) ||
      isMixedUseBuilding(__property_type) ||
      isCommercialBuilding(__property_type) ||
      // Legacy to remove
      !isLandTypeProperty(__property_type),
  ],
  [
    'gas_emissions_class',
    ({ __property_type }) =>
      isHouseOrAppt(__property_type) ||
      isMixedUseBuilding(__property_type) ||
      isCommercialBuilding(__property_type) ||
      // Legacy to remove
      !isLandTypeProperty(__property_type),
  ],
  [
    'consumption',
    ({ __property_type }) =>
      isHouseOrAppt(__property_type) ||
      isMixedUseBuilding(__property_type) ||
      isCommercialBuilding(__property_type) ||
      // Legacy to remove
      !isLandTypeProperty(__property_type),
  ],
  [
    'emissions',
    ({ __property_type }) =>
      isHouseOrAppt(__property_type) ||
      isMixedUseBuilding(__property_type) ||
      isCommercialBuilding(__property_type) ||
      // Legacy to remove
      !isLandTypeProperty(__property_type),
  ],
  [
    'energy_reference_year',
    ({ __property_type }) =>
      isHouseOrAppt(__property_type) ||
      isMixedUseBuilding(__property_type) ||
      isCommercialBuilding(__property_type) ||
      // Legacy to remove
      !isLandTypeProperty(__property_type),
  ],
  [
    'energy_inspection_date',
    ({ __property_type }) =>
      isHouseOrAppt(__property_type) ||
      isMixedUseBuilding(__property_type) ||
      isCommercialBuilding(__property_type) ||
      // Legacy to remove
      !isLandTypeProperty(__property_type),
  ],
  [
    'estimated_lower_energy_cost',
    ({ __property_type }) =>
      isHouseOrAppt(__property_type) ||
      isMixedUseBuilding(__property_type) ||
      isCommercialBuilding(__property_type) ||
      // Legacy to remove
      !isLandTypeProperty(__property_type),
  ],
  [
    'estimated_higher_energy_cost',
    ({ __property_type }) =>
      isHouseOrAppt(__property_type) ||
      isMixedUseBuilding(__property_type) ||
      isCommercialBuilding(__property_type) ||
      // Legacy to remove
      !isLandTypeProperty(__property_type),
  ],
  [
    'energy_certificate_version',
    ({ __property_type }) =>
      isHouseOrAppt(__property_type) ||
      isMixedUseBuilding(__property_type) ||
      isCommercialBuilding(__property_type) ||
      // Legacy to remove
      !isLandTypeProperty(__property_type),
  ],
  [
    'construction_title',
    ({ __property_type }) => !isLandTypeProperty(__property_type),
  ],
  [
    'construction_year',
    ({ __property_type }) =>
      isHouseOrAppt(__property_type) ||
      isMixedUseBuilding(__property_type) ||
      isCommercialBuilding(__property_type) ||
      // Legacy to remove
      !isLandTypeProperty(__property_type),
  ],
  [
    'renovation_year',
    ({ __property_type }) =>
      isHouseOrAppt(__property_type) ||
      isMixedUseBuilding(__property_type) ||
      isCommercialBuilding(__property_type) ||
      // Legacy to remove
      !isLandTypeProperty(__property_type),
  ],
  [
    'building_standard',
    ({ __property_type }) =>
      isHouseOrAppt(__property_type) ||
      isMixedUseBuilding(__property_type) ||
      isCommercialBuilding(__property_type) ||
      // Legacy to remove
      !isLandTypeProperty(__property_type),
  ],
  [
    'building_condition',
    ({ __property_type }) =>
      isHouseOrAppt(__property_type) ||
      isMixedUseBuilding(__property_type) ||
      isCommercialBuilding(__property_type) ||
      // Legacy to remove
      !isLandTypeProperty(__property_type),
  ],
  [
    'minergie_code',
    ({ __property_type }) =>
      isHouseOrAppt(__property_type) ||
      isMixedUseBuilding(__property_type) ||
      isCommercialBuilding(__property_type) ||
      // Legacy to remove
      !isLandTypeProperty(__property_type),
  ],
]);

export const getPropertyDetailsFormDefinition =
  (
    useMinergieCode: boolean,
    currency: string | null | undefined,
    countryCode: string,
    roomsAllowsDecimals: boolean,
    locale: IntlLocale,
    compactMode?: boolean,
    isCMA?: boolean,
    isEsTenant?: boolean,
  ): FormDefinitionType<PropertyFormDetailsData, PropertyFormContext> =>
  ({ t, context }) => {
    const fields = getDetailsFields(
      t,
      context,
      useMinergieCode,
      currency,
      countryCode,
      roomsAllowsDecimals,
      locale,
      compactMode,
    );

    fields.forEach(field => {
      if ('name' in field) {
        const renderFn = propertyDetailsConditionalFields.get(
          field.name as any,
        );

        if (renderFn != null) {
          // Apply CMA + ES tenant surface field logic
          if (isCMA && field.name === 'living_surface') {
            field.render = data => {
              const baseCondition = renderFn(data);
              // In CMA + ES: hide living_surface
              if (isEsTenant) {
                return false;
              }
              return baseCondition;
            };
          } else if (isCMA && field.name === 'built_surface') {
            field.render = data => {
              const baseCondition = renderFn(data);
              // In CMA + non-ES: hide built_surface
              if (!isEsTenant) {
                return false;
              }
              return baseCondition;
            };
          } else {
            field.render = renderFn;
          }
        }
      }
    });

    return fields;
  };

export const preparePropertyDetailsFormData = (
  data: PropertyFormDetailsData,
  useMinergieCode: boolean,
): Properties_Set_Input => {
  const {
    energy_consumption_class,
    gas_emissions_class,
    consumption,
    emissions,
    energy_reference_year,
    energy_inspection_date,
    estimated_lower_energy_cost,
    estimated_higher_energy_cost,
    energy_certificate_version,
    minergie_code,
    ...rest
  } = data;

  const payloadProperty: Properties_Set_Input = rest;

  if (useMinergieCode) {
    payloadProperty.minergie_code = minergie_code;
  } else {
    payloadProperty.energy_consumption_class = energy_consumption_class;
    payloadProperty.gas_emissions_class = gas_emissions_class;
    payloadProperty.consumption = consumption;
    payloadProperty.emissions = emissions;
    payloadProperty.energy_reference_year = energy_reference_year;
    payloadProperty.energy_inspection_date = energy_inspection_date;
    payloadProperty.estimated_lower_energy_cost = estimated_lower_energy_cost;
    payloadProperty.estimated_higher_energy_cost = estimated_higher_energy_cost;
    payloadProperty.energy_certificate_version = energy_certificate_version;
  }

  return Object.keys(payloadProperty).reduce(
    (acc: Properties_Set_Input, item): Properties_Set_Input => {
      const key = item as keyof typeof payloadProperty;
      const value = payloadProperty[key];
      acc[key] = value;

      const renderFn = propertyDetailsConditionalFields.get(key as any);
      if (renderFn && !renderFn(data)) {
        // We unset the value if the field is not rendered.
        acc[key] = null;
      }

      return acc;
    },
    {},
  );
};

export const getDefaultPropertyDetailsFormValues = (
  property: PropertyFormDetailsFragment,
): PropertyFormDetailsData => ({
  __main_type: property.__property_type?.main_type ?? '',
  __property_type:
    property.__property_type == null
      ? null
      : {
          name: property.__property_type.name,
          main_type: property.__property_type.main_type,
        },
  property_type_id: property.property_type_id,
  built_surface: property.built_surface,
  usable_surface: property.usable_surface,
  living_surface: property.living_surface,
  gross_floor_surface: property.gross_floor_surface,
  basement_surface: property.basement_surface,
  balcony_surface: property.balcony_surface,
  terrace_surface: property.terrace_surface,
  land_surface: property.land_surface,
  garden_surface: property.garden_surface,
  commercial_surface: property.commercial_surface,
  residential_surface: property.residential_surface,
  weighted_floor_surface: property.weighted_floor_surface,
  number_of_rooms: property.number_of_rooms,
  number_of_bedrooms: property.number_of_bedrooms,
  number_of_bathrooms: property.number_of_bathrooms,
  number_of_toilets: property.number_of_toilets,
  heating: property.heating,
  heating_distribution: property.heating_distribution,
  cooling: property.cooling,
  micro_location: property.micro_location,
  macro_location: property.macro_location,
  construction_year: property.construction_year,
  renovation_year: property.renovation_year,
  building_condition: property.building_condition,
  building_standard: property.building_standard,
  minergie_code: property.minergie_code,
  energy_certificate_version: property.energy_certificate_version,
  energy_reference_year: property.energy_reference_year,
  energy_inspection_date: property.energy_inspection_date,
  estimated_lower_energy_cost: property.estimated_lower_energy_cost,
  estimated_higher_energy_cost: property.estimated_higher_energy_cost,
  energy_consumption_class: property.energy_consumption_class,
  gas_emissions_class: property.gas_emissions_class,
  consumption: property.consumption,
  emissions: property.emissions,
  number_of_residential_units: property.number_of_residential_units,
  number_of_commercial_units: property.number_of_commercial_units,
  number_of_floors: property.number_of_floors,
  floor_of_flat: property.floor_of_flat,
  number_of_lifts: property.number_of_lifts,
  side: property.side,
  is_oriented_north: property.is_oriented_north,
  is_oriented_south: property.is_oriented_south,
  is_oriented_east: property.is_oriented_east,
  is_oriented_west: property.is_oriented_west,
  number_of_indoor_parkings: property.number_of_indoor_parkings,
  number_of_outdoor_parkings: property.number_of_outdoor_parkings,
  plot_number: property.plot_number,
  building_volume: property.building_volume,
});
